import { Tag } from '@jouzen/ecom-components/v3/Tag';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import { TESTIMONIAL_CARDS } from '@/app/[locale]/membership/_data/TestimonialData';
import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';

export interface QuoteCardsProps {
  readonly tag: MessageKey;
  readonly quote: MessageKey;
  readonly name: string;
  readonly personsPhoto: string;
  readonly photoAlt: MessageKey;
}

export const QuoteCard = ({
  tag,
  quote,
  name,
  personsPhoto,
  photoAlt,
}: QuoteCardsProps) => {
  const t = useTranslations();
  return (
    <div className="rounded-2xl bg-sandstone-400 p-6 md:max-w-56 md:p-7 lg:max-w-72 lg:p-10 xl:max-w-96">
      <div className="pb-6 md:pb-12">
        <Tag variant="darkSandstone" size="small">
          {t(tag)}
        </Tag>
      </div>
      <blockquote>
        <Typography
          className="pb-6 text-body-lg leading-tight before:block before:content-[open-quote] after:content-[close-quote] xxl:text-heading-sm"
          color="inherit"
          Element="p"
          font="serif"
          weight="thin"
        >
          <RichText messageKey={quote} />
        </Typography>

        <footer className="flex flex-row items-center">
          <Image
            src={personsPhoto}
            alt={photoAlt}
            width={60}
            height={60}
            className="fit h-8 w-8 rounded-full lg:h-15 lg:w-15"
          />
          <Typography
            className="text-body-small pl-4 font-bold lg:text-xl"
            color="inherit"
            font="sans"
          >
            {name}
          </Typography>
        </footer>
      </blockquote>
    </div>
  );
};

export const Testimonials = () => {
  const t = useTranslations();
  return (
    <section data-cy="testimonials">
      <div>
        <Typography
          Element="h2"
          font="serif"
          className="pt-10 pb-6 text-center text-4xl md:pt-20 md:pb-10 md:text-5xl lg:text-6xl xxl:pb-16"
        >
          {t('membership_real_members')}
        </Typography>
      </div>
      <div className="flex flex-col items-stretch justify-evenly gap-4 px-6 pt-6 pb-16 md:flex-row md:justify-evenly md:px-7 md:py-20">
        {TESTIMONIAL_CARDS.map((card) => {
          return (
            <QuoteCard
              tag={card.tag}
              quote={card.quote}
              name={card.name}
              personsPhoto={card.personsPhoto}
              photoAlt={card.photoAlt}
              key={card.name}
            />
          );
        })}
      </div>
    </section>
  );
};
